import { supabase } from '../../../lib/supabase';
import type { DdtPrintTemplate, DdtTemplateLanguage } from '../../../lib/stores/ddtPrintTemplateStore';
import type { ManualOrder } from '../../../lib/types/orders';
import { fetchCommercialNames } from '../../../lib/hooks/useProductCommercialNames';
import { formatQuantity, type QuantityFormatOptions } from '../../../lib/utils/quantityFormat';
import { resolveProductUnit } from '../../../lib/constants/units';

/**
 * Stampa / PDF della conferma d'ordine (ordini manuali).
 *
 * Apre il documento in una finestra con il dialogo di stampa del browser: da lì
 * si stampa o si salva come PDF ("Salva come PDF"). Stesso meccanismo delle
 * stampe DDT.
 *
 * Intestazione (logo, ragione sociale, indirizzo, Codice SDI, banche, P.IVA,
 * email) e lingua delle etichette arrivano dal template di stampa scelto — gli
 * stessi template di Impostazioni → Template stampa DDT, così l'org non deve
 * reinserire i dati aziendali. Senza template: solo il nome dell'organizzazione.
 *
 * Layout sul modello della conferma d'ordine del gestionale precedente di Say
 * Farm: titolo, box Numero/Data ordine, box Spett.le, fascia Pagamento/
 * Destinazione, tabella Cod. articolo/Descrizione/UM/Quantità/Prezzo/Sconto/
 * Importo/IVA, riepilogo totali e note.
 */

interface Labels {
  title: string;
  orderNumber: string;
  orderDate: string;
  page: string;
  to: string;
  vat: string;
  payment: string;
  destination: string;
  shipping: string;
  code: string;
  description: string;
  um: string;
  qty: string;
  price: string;
  discount: string;
  amount: string;
  vatCol: string;
  subtotal: string;
  totalDiscount: string;
  shippingCost: string;
  taxAmount: string;
  total: string;
  notes: string;
  commercialName: string;
  packaging: string;
}

const LABELS: Record<DdtTemplateLanguage, Labels> = {
  it: {
    title: "CONFERMA D'ORDINE", orderNumber: 'Numero Ordine', orderDate: 'Data Ordine', page: 'Pag.',
    to: 'Spett.le', vat: 'P.IVA', payment: 'Pagamento', destination: 'Destinazione merce', shipping: 'Spedizione',
    code: 'Cod. articolo', description: 'Descrizione', um: 'UM', qty: 'Quantità', price: 'Prezzo',
    discount: 'Sconto', amount: 'Importo', vatCol: 'IVA',
    subtotal: 'Imponibile', totalDiscount: 'Sconto', shippingCost: 'Spese di trasporto', taxAmount: 'IVA',
    total: 'Totale ordine', notes: 'Note', commercialName: 'Nome commerciale', packaging: 'Confezionamento',
  },
  en: {
    title: 'ORDER CONFIRMATION', orderNumber: 'Order number', orderDate: 'Order date', page: 'Page',
    to: 'To', vat: 'VAT number', payment: 'Payment', destination: 'Delivery address', shipping: 'Shipping',
    code: 'Item code', description: 'Description', um: 'UOM', qty: 'Quantity', price: 'Price',
    discount: 'Discount', amount: 'Amount', vatCol: 'VAT',
    subtotal: 'Taxable amount', totalDiscount: 'Discount', shippingCost: 'Shipping costs', taxAmount: 'VAT',
    total: 'Order total', notes: 'Notes', commercialName: 'Trade name', packaging: 'Packaging',
  },
  fr: {
    title: 'CONFIRMATION DE COMMANDE', orderNumber: 'Num&#233;ro de commande', orderDate: 'Date de commande', page: 'Page',
    to: '&#192; l&#8217;attention de', vat: 'N. TVA', payment: 'Paiement', destination: 'Lieu de livraison', shipping: 'Exp&#233;dition',
    code: 'Code article', description: 'Description', um: 'UM', qty: 'Quantit&#233;', price: 'Prix',
    discount: 'Remise', amount: 'Montant', vatCol: 'TVA',
    subtotal: 'Montant HT', totalDiscount: 'Remise', shippingCost: 'Frais de transport', taxAmount: 'TVA',
    total: 'Total commande', notes: 'Notes', commercialName: 'Nom commercial', packaging: 'Conditionnement',
  },
};

export interface ManualOrderPrintData {
  order: ManualOrder;
  organizationName: string;
  client: Record<string, unknown> | null;
  paymentLabel: string;
  unitsByProduct: Record<string, string>;
  commercialNames: Record<string, string>;
  /**
   * Senza la nota automatica «UM: …» sotto l'articolo (la scrive flusia-core
   * nelle note della riga quando l'ordine nasce da un'offerta, e ripete la
   * colonna U.M.). warehouse_settings.order_print_hide_unit_note, per organizzazione.
   */
  hideUnitNote?: boolean;
  /** Confezionamento per riga d'ordine «5 × 1 kg» (warehouse_settings.line_packaging_split_enabled) */
  packagingByOrderItem?: Record<string, string>;
}

function esc(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const num = (n: unknown, min = 2, max = 2) =>
  Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: min, maximumFractionDigits: max });
// Nota automatica dell'ordine da offerta (flusia-core orders/repository.ts itemPayload):
// solo «UM: <unità>», senza altro testo scritto a mano
const isUnitNote = (notes: string) => /^UM: [^\n]*$/.test(notes.trim());
const qtyFmt = (n: unknown, unit?: string | null, unitDisplay: QuantityFormatOptions['unitDisplay'] = 'suffix') =>
  formatQuantity(Number(n || 0), { unit, unitDisplay });
const dateFmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

/**
 * Raccoglie i dati che l'ordine non porta con sé: anagrafica cliente completa,
 * metodo di pagamento predefinito del cliente, UDM e nome commerciale degli
 * articoli, nome dell'organizzazione. Letture tolleranti: un dato mancante
 * lascia vuoto il suo campo, non blocca la stampa.
 */
export async function loadManualOrderPrintData(order: ManualOrder, showCommercialName: boolean): Promise<ManualOrderPrintData> {
  const productIds = [...new Set((order.items || []).map(i => i.product_id).filter(Boolean))];

  const [orgRes, clientRes, productsRes, commercialNames] = await Promise.all([
    supabase.from('organizations').select('name').eq('id', order.organization_id).maybeSingle(),
    order.client_id
      ? supabase
          .from('clients')
          .select('business_name, street_address, street_number, postal_code, city, province, country, vat_number, tax_code, default_payment_method_id')
          .eq('id', order.client_id)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    productIds.length > 0
      ? supabase.from('products').select('id, unit_of_measure_1').in('id', productIds)
      : Promise.resolve({ data: [], error: null }),
    showCommercialName ? fetchCommercialNames(productIds) : Promise.resolve({}),
  ]);

  const client = (clientRes.data || null) as Record<string, unknown> | null;
  let paymentLabel = '';
  const paymentMethodId = client?.default_payment_method_id as string | undefined;
  if (paymentMethodId) {
    const { data } = await supabase.from('payment_methods').select('name').eq('id', paymentMethodId).maybeSingle();
    paymentLabel = (data?.name as string) || '';
  }

  const unitsByProduct: Record<string, string> = {};
  for (const p of (productsRes.data || []) as Array<{ id: string; unit_of_measure_1: string | null }>) {
    if (p.unit_of_measure_1) unitsByProduct[p.id] = p.unit_of_measure_1;
  }

  return {
    order,
    organizationName: (orgRes.data?.name as string) || '',
    client,
    paymentLabel,
    unitsByProduct,
    commercialNames,
  };
}

function addressLines(a?: ManualOrder['billing_address']): string[] {
  if (!a) return [];
  return [
    [a.address_1, a.address_2].filter(Boolean).join(' '),
    [a.postcode, a.city, a.state ? `(${a.state})` : ''].filter(Boolean).join(' '),
    a.country || '',
  ].filter(Boolean);
}

/**
 * `targetWindow`: finestra già aperta al clic dell'utente. Aprirla solo dopo il
 * caricamento dei dati (await) può farla bloccare come popup dal browser.
 */
export function printManualOrder(template: DdtPrintTemplate | null, data: ManualOrderPrintData, targetWindow?: Window | null): void {
  const win = targetWindow || window.open('', '_blank', 'width=900,height=700');
  if (!win) return;

  const t = LABELS[template?.language || 'it'] || LABELS.it;
  const { order, client } = data;

  // Intestazione aziendale
  const companyLines: string[] = [];
  const companyName = template?.company_name || data.organizationName;
  if (template) {
    if (template.address_line1) companyLines.push(esc(template.address_line1));
    if (template.address_line2) companyLines.push(esc(template.address_line2));
    if (template.sdi_code) companyLines.push('Codice SDI ' + esc(template.sdi_code));
    for (const bank of (template.bank_details || '').split('\n').map(l => l.trim()).filter(Boolean)) companyLines.push(esc(bank));
    const fiscal = [template.vat_number ? 'P.iva ' + esc(template.vat_number) : '', template.tax_code ? 'C.F. ' + esc(template.tax_code) : ''].filter(Boolean);
    if (fiscal.length) companyLines.push(fiscal.join(' - '));
    if (template.email) companyLines.push(esc(template.email));
  }

  // Cliente: indirizzo di fatturazione dell'ordine, altrimenti anagrafica cliente
  const clientName = (order.billing_address?.company as string) || order.client?.business_name || (client?.business_name as string) || '';
  const billing = addressLines(order.billing_address);
  const clientLines = billing.length > 0
    ? billing
    : [
        [client?.street_address, client?.street_number].filter(Boolean).join(', '),
        [client?.postal_code, client?.city, client?.province ? `(${client.province})` : ''].filter(Boolean).join(' '),
        (client?.country as string) || '',
      ].filter(Boolean);
  const clientVat = order.client?.vat_number || (client?.vat_number as string) || (client?.tax_code as string) || '';

  const shipping = addressLines(order.shipping_address);
  const shippingName = [order.shipping_address?.company, [order.shipping_address?.first_name, order.shipping_address?.last_name].filter(Boolean).join(' ')].filter(Boolean)[0] || '';
  const shippingInfo = [order.shipping_method, order.carrier].filter(Boolean).join(' - ');

  const css = `
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; font-size: 10px; }
    .head { display: flex; gap: 8mm; align-items: flex-start; margin-bottom: 3mm; }
    .logo { width: 45mm; flex-shrink: 0; }
    .logo img { max-width: 40mm; max-height: 30mm; object-fit: contain; }
    .company { font-size: 10px; line-height: 1.45; letter-spacing: 0.2px; }
    .company b { font-size: 11px; }
    .row { display: flex; gap: 6mm; align-items: stretch; margin-bottom: 3mm; }
    .box { border: 1px solid #000; border-radius: 3mm; padding: 2.5mm 4mm; }
    .title { font-size: 15px; font-weight: bold; font-style: italic; margin: 0 0 3mm; }
    .lbl { font-weight: bold; font-style: italic; }
    .orderbox { display: grid; grid-template-columns: 1fr 1fr auto; gap: 2mm 6mm; }
    .orderbox .val { font-weight: bold; }
    .to { flex: 1; line-height: 1.6; }
    .band { border: 1px solid #000; border-radius: 3mm; margin-bottom: 3mm; display: grid; grid-template-columns: 1fr 1fr; }
    .band > div { padding: 2mm 4mm; }
    .band > div + div { border-left: 1px solid #000; }
    table.lines { width: 100%; border-collapse: collapse; border: 1px solid #000; }
    table.lines th { border: 1px solid #000; font-style: italic; font-size: 10.5px; padding: 2mm 1.5mm; }
    table.lines td { border-left: 1px solid #000; border-right: 1px solid #000; padding: 1mm 1.5mm; vertical-align: top; }
    table.lines tbody tr:last-child td { border-bottom: 1px solid #000; }
    .num { text-align: right; white-space: nowrap; }
    .c { text-align: center; }
    .code { font-size: 9px; }
    .sub { font-size: 8px; color: #333; }
    .filler td { height: 40mm; }
    .totals { margin-left: auto; margin-top: 3mm; width: 80mm; border: 1px solid #000; border-radius: 3mm; padding: 2mm 4mm; }
    .totals div { display: flex; justify-content: space-between; padding: 0.6mm 0; }
    .totals .grand { font-weight: bold; font-size: 11.5px; border-top: 1px solid #000; margin-top: 1mm; padding-top: 1.5mm; }
    .notes { margin-top: 3mm; border: 1px solid #000; border-radius: 3mm; padding: 2mm 4mm; min-height: 12mm; }
    @page { size: portrait; margin: 10mm; }
  `;

  let html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + esc(`${t.title} ${order.order_number}`) + '</title><style>' + css + '</style></head><body>';

  html += '<div class="head">';
  html += '<div class="logo">' + (template?.logo_url ? '<img src="' + esc(template.logo_url) + '" alt="">' : '') + '</div>';
  html += '<div class="company"><b>' + esc(companyName) + '</b>' + (companyLines.length ? '<br>' + companyLines.join('<br>') : '') + '</div>';
  html += '</div>';

  html += '<div class="row">';
  html += '<div style="flex:1;display:flex;flex-direction:column;justify-content:flex-end;">';
  html += '<p class="title">' + t.title + '</p>';
  html += '<div class="box orderbox">';
  html += '<span class="lbl">' + t.orderNumber + ':</span><span class="lbl">' + t.orderDate + ':</span><span></span>';
  html += '<span class="val">' + esc(order.order_number) + '</span><span class="val">' + esc(dateFmt(order.order_date || order.created_at)) + '</span><span>' + t.page + ' 1</span>';
  html += '</div></div>';
  html += '<div class="box to"><div class="lbl">' + t.to + '</div><div>' + esc(clientName) + '</div>';
  for (const l of clientLines) html += '<div>' + esc(l) + '</div>';
  if (clientVat) html += '<div><span class="lbl">' + t.vat + '</span>&nbsp;&nbsp;' + esc(clientVat) + '</div>';
  html += '</div></div>';

  html += '<div class="band">';
  html += '<div><span class="lbl">' + t.payment + '</span>&nbsp;&nbsp;' + esc(data.paymentLabel) + '</div>';
  html += '<div><span class="lbl">' + t.destination + '</span>' + (shippingName || shipping.length ? '<br>' + [shippingName, ...shipping].filter(Boolean).map(esc).join('<br>') : '')
    + (shippingInfo ? '<br><span class="lbl">' + t.shipping + '</span> ' + esc(shippingInfo) : '') + '</div>';
  html += '</div>';

  html += '<table class="lines"><thead><tr>';
  html += '<th style="width:14%;">' + t.code + '</th><th>' + t.description + '</th><th style="width:6%;">' + t.um + '</th>';
  html += '<th style="width:9%;">' + t.qty + '</th><th style="width:10%;">' + t.price + '</th><th style="width:9%;">' + t.discount + '</th>';
  html += '<th style="width:11%;">' + t.amount + '</th><th style="width:6%;">' + t.vatCol + '</th>';
  html += '</tr></thead><tbody>';
  for (const item of order.items || []) {
    const commercial = data.commercialNames[item.product_id];
    html += '<tr>';
    html += '<td class="code">' + esc(item.product_sku) + '</td>';
    html += '<td>' + esc(item.product_name)
      + (commercial && commercial !== item.product_name ? '<div class="sub">' + t.commercialName + ': ' + esc(commercial) + '</div>' : '')
      + (item.pack_weight_kg ? '<div class="sub">' + qtyFmt(item.pack_weight_kg, 'kg') + ' / ' + esc(item.sale_unit || 'confezione') + ' · ' + qtyFmt(Number(item.quantity) * Number(item.pack_weight_kg), 'kg') + '</div>' : '')
      + (item.id && data.packagingByOrderItem?.[item.id] ? '<div class="sub"><b>' + t.packaging + ':</b> ' + esc(data.packagingByOrderItem[item.id]) + '</div>' : '')
      + (item.notes && !(data.hideUnitNote && isUnitNote(item.notes)) ? '<div class="sub">' + esc(item.notes) + '</div>' : '') + '</td>';
    html += '<td class="c">' + esc(item.sale_unit || data.unitsByProduct[item.product_id] || '') + '</td>';
    // UM nella sua colonna: qui solo per il punto delle migliaia (a pezzi niente punto)
    html += '<td class="num">' + qtyFmt(item.quantity, item.sale_unit || resolveProductUnit({ unit_of_measure_1: data.unitsByProduct[item.product_id] }), 'none') + '</td>';
    html += '<td class="num">' + num(item.unit_price, 2, 4) + '</td>';
    html += '<td class="num">' + (Number(item.discount_amount) > 0 ? num(item.discount_amount) : '') + '</td>';
    html += '<td class="num">' + num(item.line_total) + '</td>';
    html += '<td class="c">' + (item.tax_rate !== null && item.tax_rate !== undefined ? esc(`${Number(item.tax_rate)}%`) : '') + '</td>';
    html += '</tr>';
  }
  html += '<tr class="filler">' + '<td></td>'.repeat(8) + '</tr>';
  html += '</tbody></table>';

  html += '<div class="totals">';
  html += '<div><span>' + t.subtotal + '</span><span>' + num(order.subtotal) + '</span></div>';
  if (Number(order.discount_amount) > 0) html += '<div><span>' + t.totalDiscount + '</span><span>-' + num(order.discount_amount) + '</span></div>';
  if (Number(order.shipping_cost) > 0) html += '<div><span>' + t.shippingCost + '</span><span>' + num(order.shipping_cost) + '</span></div>';
  html += '<div><span>' + t.taxAmount + '</span><span>' + num(order.tax_amount) + '</span></div>';
  html += '<div class="grand"><span>' + t.total + '</span><span>' + esc(order.currency || 'EUR') + ' ' + num(order.total) + '</span></div>';
  html += '</div>';

  if (order.customer_notes) {
    html += '<div class="notes"><span class="lbl">' + t.notes + '</span><br>' + esc(order.customer_notes).replace(/\n/g, '<br>') + '</div>';
  }

  html += '</body></html>';
  win.document.write(html);
  win.document.close();
  win.focus();
  // Attende il caricamento del logo prima di aprire il dialogo di stampa
  setTimeout(() => win.print(), 400);
}
